import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PlayerStats } from '../models/PlayerStats';
import PlayerImage from '../components/PlayerImage';
import PlayerTable from '../components/PlayerTable';
import HeadToHeadSection from '../components/HeadToHeadSection';
import { Link, useLocation } from 'react-router-dom';
import { Account } from '../models/Account';
import { fetchPlayersByPlayerName } from '../services/AccountService';
import { fetchSeasonsPlayed } from "../services/PlayerStatService";
import { fetchPlayerBySteamIdAndSeason } from "../services/PlayerStatService";
import debounce from 'lodash/debounce';
import { useDarkMode } from '../hooks/useDarkmode';
import '../styles/compare-search.css';

type PlayerWithSeason = {
  stats: PlayerStats;
  selectedSeason: number;
};

const ComparePage = () => {
  const { t } = useTranslation(['pages', 'common']);
  const [playerSeasons, setPlayerSeasons] = useState<{ [steamId: string]: number[] }>({});
  const [players, setPlayers] = useState<PlayerWithSeason[]>([]);
  const [searchResults, setSearchResults] = useState<Account[]>([]);
  const [error, setError] = useState<any>();
  const [isLoading, setIsLoading] = useState(false);
  const [isUrlLoaded, setIsUrlLoaded] = useState(false); // New state to track if URL params have been loaded
  const inputElem = useRef<HTMLInputElement>(null);
  const handleSearch = debounce(inputVal => fetchSearchResults(inputVal), 500);
  const location = useLocation();

  const isDark = useDarkMode();

  // Extract player IDs from URL query parameters - run only once on initial load
  useEffect(() => {
    const loadPlayersFromQueryParams = async () => {
      if (isUrlLoaded) return; // Skip if already loaded from URL
      
      const params = new URLSearchParams(location.search);
      const playerIds = params.get('players');
      
      if (playerIds) {
        setIsLoading(true);
        
        // Clear existing data completely
        setPlayers([]);
        setPlayerSeasons({});
        
        // Process unique IDs
        const uniqueIds = Array.from(new Set(playerIds.split(',')));
        console.log("Loading unique IDs:", uniqueIds);
        
        try {
          // Limit to max 2 players
          const idsToLoad = uniqueIds.slice(0, 2);
          
          // Create an array to hold the loaded players
          const loadedPlayers: PlayerWithSeason[] = [];
          const loadedSeasons: { [steamId: string]: number[] } = {};
          
          // Load each player's data
          for (const steamId of idsToLoad) {
            try {
              const seasons = await fetchSeasonsPlayed(steamId);
              const highestSeason = seasons.sort((a, b) => b - a)[0];
              const playerStats = await fetchPlayerBySteamIdAndSeason(steamId, highestSeason);
              
              if (playerStats) {
                loadedPlayers.push({ 
                  stats: playerStats, 
                  selectedSeason: highestSeason 
                });
                
                loadedSeasons[steamId] = seasons;
              }
            } catch (err) {
              console.error(`Error loading player ${steamId}:`, err);
            }
          }
          
          // Update state only once with all loaded data
          setPlayers(loadedPlayers);
          setPlayerSeasons(loadedSeasons);
          
        } catch (err) {
          setError(err);
          console.error('Error loading players from URL:', err);
        } finally {
          setIsLoading(false);
          setIsUrlLoaded(true); // Mark as loaded
        }
      } else {
        setIsUrlLoaded(true); // Mark as loaded even if no players in URL
      }
    };
    
    loadPlayersFromQueryParams();
  }, [location.search, isUrlLoaded]); // Only run when URL changes or on first load

  const fetchSearchResults = async (query: string) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    try {
      const results = await fetchPlayersByPlayerName(query);
      setSearchResults(results || []);
      setError('');
    } catch (err) {
      setSearchResults([]);
      setError(err);
    }
  };

  const selectPlayer = async (player: Account) => {
    try {
      // Check if player is already in the list
      if (players.some(p => p.stats.steamID === player.steamID)) {
        return; // Player is already in the comparison
      }
      
      // Fetch player data
      const seasons = await fetchSeasonsPlayed(player.steamID);
      const highestSeason = seasons.sort((a, b) => b - a)[0];
      const playerStats = await fetchPlayerBySteamIdAndSeason(player.steamID, highestSeason);

      if (playerStats) {
        // Limit to max 2 players for head-to-head comparison
        if (players.length >= 2) {
          // Replace the first player in the list
          setPlayers(prevPlayers => [
            prevPlayers[1],
            { stats: playerStats, selectedSeason: highestSeason }
          ]);
        } else {
          // Add new player
          setPlayers(prevPlayers => [
            ...prevPlayers,
            { stats: playerStats, selectedSeason: highestSeason }
          ]);
        }
        
        // Store seasons information
        setPlayerSeasons(prevSeasons => ({ 
          ...prevSeasons, 
          [player.steamID]: seasons 
        }));
      } else {
        console.error('No stats found for player with ID:', player.steamID);
      }
    } catch (err) {
      setError(err);
    }

    setSearchResults([]);
  };

  const handleSeasonChange = async (steamID: string, season: number) => {
    try {
      const playerStats = await fetchPlayerBySteamIdAndSeason(steamID, season);
      if (playerStats) {
        setPlayers(prevPlayers =>
          prevPlayers.map(playerWithSeason =>
            playerWithSeason.stats.steamID === steamID
              ? { ...playerWithSeason, stats: playerStats, selectedSeason: season }
              : playerWithSeason
          )
        );
      }
    } catch (err) {
      setError(err);
    }
  };

  const handleRemovePlayer = (steamID: string) => {
    setPlayers(prevPlayers => prevPlayers.filter(player => player.stats.steamID !== steamID));
  };

  // Check if we have exactly 2 players for head-to-head comparison
  const showHeadToHead = players.length === 2;

  return (
    <div className={`container ${isDark ? 'text-light' : ''}`}>
      <h2 className="my-4">{t('pages:compare.title')}</h2>

      {isLoading ? (
        <div className="text-center my-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common:loading')}</span>
          </div>
          <p className="mt-2">{t('pages:compare.loadingPlayers')}</p>
        </div>
      ) : (
        <>
          <div className="row mb-4">
            <div className="col-md-6">
              <div className="search-section">
                <div className="input-group mb-3">
                  <input
                    type="text"
                    className={`form-control ${isDark ? 'text-light compare-search-input' : ''}`}
                    placeholder={t('pages:compare.addPlayer')}
                    ref={inputElem}
                    onChange={() => handleSearch(inputElem.current?.value)}
                    style={isDark ? { 
                      backgroundColor: 'var(--bg-secondary)', 
                      color: '#ffffff',
                      border: '1px solid var(--border-primary)'
                    } : {}}
                  />
                </div>

                {error && (
                  <div className="alert alert-danger" role="alert">
                    {t('pages:compare.databaseError')}
                  </div>
                )}
                <div className="row">
                  {searchResults.map((result, index) => (
                    <div className="col-sm-6 col-md-3" key={index}>
                      <div className="mb-4">
                        <button
                          className="btn btn-primary text-truncate"
                          style={{ width: 100 }}
                          onClick={() => selectPlayer(result)}
                        >
                          {result.playerName}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="row">
                {players.map((playerWithSeason) => (
                  <div className='col-sm-12 col-md-6' key={playerWithSeason.stats.steamID}>
                    <div 
                      className={`card text-center m-1 ${isDark ? 'text-light' : ''}`}
                      style={isDark ? { 
                        backgroundColor: 'var(--bg-card)', 
                        border: '1px solid var(--border-primary)' 
                      } : {}}
                    >
                      <div className='card-body'>
                        <div className='text-end'>
                          <button
                            className='btn btn-danger btn-sm'
                            onClick={() => handleRemovePlayer(playerWithSeason.stats.steamID)}
                          >
                            X
                          </button>
                        </div>
                        <Link
                          to={`/player/${playerWithSeason.stats.steamID}`}
                          className={`text-decoration-none ${isDark ? 'text-light' : 'text-black'}`}
                        >
                          <PlayerImage id={playerWithSeason.stats.steamID} width={100} />
                          <h5 className='card-title'>
                            {playerWithSeason.stats.account
                              ? playerWithSeason.stats.account.playerName
                              : playerWithSeason.stats.steamID}
                          </h5>
                        </Link>
                        <select
                          value={playerWithSeason.selectedSeason}
                          onChange={(e) =>
                            handleSeasonChange(playerWithSeason.stats.steamID, parseInt(e.target.value))
                          }
                          className={`form-select form-select-sm mt-2 ${isDark ? 'text-light' : ''}`}
                          style={isDark ? { 
                            backgroundColor: 'var(--bg-secondary)', 
                            color: '#ffffff',
                            border: '1px solid var(--border-primary)'
                          } : {}}
                        >
                          {playerSeasons[playerWithSeason.stats.steamID]?.map(season => (
                            <option key={season} value={season} style={isDark ? {
                              backgroundColor: 'var(--bg-secondary)',
                              color: '#ffffff'
                            } : {}}>
                              {t('common:seasonNum', { number: season })}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Head to Head Section - Only shown when exactly 2 players are selected */}
          {showHeadToHead && (
            <div className="row mb-4">
              <div className="col-12">
                <HeadToHeadSection 
                  player1={players[0].stats} 
                  player2={players[1].stats} 
                  isDark={isDark} 
                />
              </div>
            </div>
          )}

          {/* Player Stats Table */}
          {players.length > 0 && (
            <div className="row">
              <div className="col-12">
                <PlayerTable players={players.map(p => p.stats)} />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ComparePage;